import { useMediaQuery } from '@mui/material'
import { NullableOfPoolTypeIds, useGetIdsHealthcareservicesHcsIdCaregivers } from '@toktokdoctech/tokapi-client'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { useTheme } from '@mui/material/styles'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import BlockIcon from '@mui/icons-material/Block'
import EditIcon from '@mui/icons-material/Edit'
import PersonIcon from '@mui/icons-material/Person'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useStyles from '../../Common/_styles/dataTableStyles'
import TokTokDocDataTable from '../../Common/DataTable'
import { LogoPrimaryIcon, SuperuserIcon } from '../../../assets/icons/icons'
import useUserPermissions from '../../../hooks/useUserPermissions'
import { TokTokDocDataTableDescribeRows, TokTokDocDataTableHeaders } from '../../Common/_props/TokTokDocDataTableProps'
import DataTableActions from '../../Common/dataTableActions/DataTableActions'
import { CaregiverDataTableProps, CaregiverRow } from './_types'
import useCurrentHcs from '../../../hooks/useCurrentHcs'

export const CaregiverDataTable = ({ filters }: CaregiverDataTableProps) => {
    const { id: hcsId } = useCurrentHcs()
    const { hasUserPermission } = useUserPermissions()
    const navigate = useNavigate()
    const theme = useTheme()
    const large = useMediaQuery(theme.breakpoints.up('sm'))
    const [limit, setLimit] = useState(large ? 10 : 5)
    const [page, setPage] = useState(0)
    const [sortOrder, setSortOrder] = useState<'Asc' | 'Desc'>('Asc')

    const { data, isLoading } = useGetIdsHealthcareservicesHcsIdCaregivers(hcsId, {
        ...filters,
        positions: filters.positions?.join(','),
        roles: filters.roles?.join(','),
        sortOrder,
        limit,
        skip: page * limit,
    })

    const getTooltipContent = (carePoolType: NullableOfPoolTypeIds | undefined) => {
        // internal
        if (carePoolType === NullableOfPoolTypeIds.Internal) {
            return 'Cet utilisateur est assigné à votre établissement par TokTokDoc afin de vous accompagner (secrétaire médicale, support utilisateur ou chef de projet). Si vous souhaitez le supprimer, contactez le support TokTokDoc.'
        }
        // external
        if (carePoolType === NullableOfPoolTypeIds.External) {
            return 'Cet utilisateur est assigné à votre établissement car il fait partie de votre groupement d’établissement. Si vous souhaitez le supprimer, contactez le support TokTokDoc.'
        }
        return ''
    }

    const caregivers: CaregiverRow[] = useMemo(() => {
        if (!data) return []

        return data.caregivers.map((row) => ({
            ...row,
            disabled: row.isCarePoolUser,
            tooltip: row.isCarePoolUser ? getTooltipContent(row?.carePoolType) : '',
        }))
    }, [data])

    const classes = useStyles()

    const handleItemsPerPageChange = (event: any) => {
        const { value } = event.target
        setLimit(value)
        setPage(0)
    }

    const toggleSort = () => {
        setSortOrder((previouSort) => (previouSort === 'Desc' ? 'Asc' : 'Desc'))
        setPage(0)
    }

    const headers: TokTokDocDataTableHeaders = [
        {
            label: 'Nom',
            buttonProps: {
                endIcon: sortOrder === 'Desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />,
                disableElevation: true,
                onClick: toggleSort,
            },
        },
        {
            label: 'Prénom',
        },
        {
            label: 'Profession',
        },
        {
            label: 'Fonction',
        },
        {
            label: 'Rôle',
        },
        {
            display: hasUserPermission('ADMIN'),
            label: 'Actions',
            size: 'sm',
            orientation: 'center',
        },
    ]

    const handlePaginationChange = (event: any, value: any) => {
        setPage(value)
    }

    const getCellIcon = (isEnabled: boolean, isInternalCarePoolUser: boolean) => {
        if (!isEnabled) {
            return <BlockIcon className={classes.DataTableCellIcon} />
        }
        if (isInternalCarePoolUser) {
            return <LogoPrimaryIcon className={classes.DataTableCellIcon} />
        }
        return null
    }

    const getRoleIcon = (roleCode: number) => {
        if (roleCode === 2) return <PersonIcon className={classes.DataTableCellIcon} />
        if (roleCode === 1) return <VerifiedUserIcon className={classes.DataTableCellIcon} />
        if (roleCode === 0) return <SuperuserIcon className={classes.DataTableCellIcon} />
        throw new Error(`[UserDataTable] getRoleIcon - invalid role code: ${roleCode}, expected: 0 | 1 | 2`)
    }

    const describeRows: TokTokDocDataTableDescribeRows<CaregiverRow> = (row) => {
        const internalCarePool = row.carePoolType === NullableOfPoolTypeIds.Internal

        return [
            {
                item: row.lastName.toUpperCase(),
                icon: getCellIcon(row.isEnabled, internalCarePool),
            },
            {
                item: row.firstName,
            },
            {
                item: row.professions.at(0) ?? '',
            },
            {
                item: row.position ?? '',
            },
            {
                item: row.role?.label,
                icon: row.role?.code !== undefined ? getRoleIcon(row.role.code) : undefined,
            },
            {
                display: hasUserPermission('ADMIN'),
                item: (
                    <DataTableActions
                        actions={[
                            {
                                to: `/user/${row?.id}`,
                                icon: (
                                    <EditIcon
                                        data-cy="editIcon"
                                        fontSize="small"
                                        titleAccess="Modifier un utilisateur"
                                    />
                                ),
                                disabled: row.disabled,
                            },

                            {
                                to: `/user-password/${row?.id}`,
                                icon: <VpnKeyIcon titleAccess="Modifier un mot de passe" fontSize="small" />,
                                disabled: row.disabled,
                            },
                        ]}
                    />
                ),
                orientation: 'end',
            },
        ]
    }

    return (
        <TokTokDocDataTable
            loading={isLoading}
            page={page}
            itemsPerPage={limit}
            count={data?.total}
            handlePaginationChange={handlePaginationChange}
            handleItemsPerPageChange={handleItemsPerPageChange}
            headers={headers}
            items={caregivers}
            describeRows={describeRows}
            onRowClick={(row) => navigate(`/user/${row?.id}`)}
        />
    )
}
